import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Search, UserPlus, Edit, Trash2, Users, Shield, Briefcase, User,
  Crown, Wrench, ScanSearch, BarChart3, Truck, UserCog, Sparkles, Layers, ExternalLink,
} from 'lucide-react';
import {  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import TablePagination from '../../components/TablePagination';
import GenericBulkActionsBar from '../../components/GenericBulkActionsBar';
import OrgChart from '../../components/admin/OrgChart';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icônes par slug standard (fallback Shield pour rôles custom)
const ROLE_ICON_BY_SLUG = {
  admin: Crown,
  commercial: Briefcase,
  lead_commercial: Briefcase,
  ops: Wrench,
  operateur: Wrench,
  lead_ops: UserCog,
  field_ops: Truck,
  lead_field_ops: UserCog,
  finance: BarChart3,
  comptable: BarChart3,
  auditeur: ScanSearch,
};

// Slugs réservés aux clients/chauffeurs — exclus de la vue Staff Interne
const NON_STAFF_SLUGS = new Set(['client', 'expediteur', 'chauffeur', 'driver']);

const InternalUsersManagement = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // RBAC v2 — fetch dynamique des rôles + packs (templates)
  const [rbacRoles, setRbacRoles] = useState([]);
  const [rbacPacks, setRbacPacks] = useState([]);
  const [rbacLoading, setRbacLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    role: 'operateur',
    jobTitleId: '',
    departement: '',
    manager_id: '',
    team_id: '',
    twoFactorEnabled: false
  });

  const [jobTitles, setJobTitles] = useState([]);
  const [view, setView] = useState('list');

  const existingDepartments = useMemo(
    () => [...new Set(users.map((u) => (u.departement || '').trim()).filter(Boolean))].sort(),
    [users]
  );
  const [jobTitleModalUser, setJobTitleModalUser] = useState(null);
  const [jobTitleModalValue, setJobTitleModalValue] = useState('');
  const [savingJobTitle, setSavingJobTitle] = useState(false);

  const saveJobTitle = async () => {
    if (!jobTitleModalValue) {
      toast({ title: "Poste requis", description: "Choisissez un poste", variant: "destructive" });
      return;
    }
    setSavingJobTitle(true);
    try {
      await axios.put(`${API}/internal-users/${jobTitleModalUser.id}`, { jobTitleId: jobTitleModalValue });
      toast({ title: "✅ Poste mis à jour", description: `${jobTitleModalUser.firstName} ${jobTitleModalUser.lastName}` });
      setJobTitleModalUser(null);
      fetchUsers();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Mise à jour impossible",
        variant: "destructive"
      });
    } finally {
      setSavingJobTitle(false);
    }
  };

  const fetchJobTitles = async () => {
    try {
      const res = await axios.get(`${API}/settings/job-titles?active_only=true`);
      setJobTitles(res.data?.jobTitles || []);
    } catch (e) {
      setJobTitles([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/internal-users`);
      setUsers(response.data);
      setFilteredUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching internal users:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les utilisateurs internes",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchJobTitles();
  }, []);

  // RBAC v2 — fetch dynamique des rôles staff + packs
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRbacLoading(true);
      try {
        const [rolesRes, packsRes] = await Promise.all([
          axios.get(`${API}/rbac/roles`),
          axios.get(`${API}/rbac/packs`),
        ]);
        if (cancelled) return;
        const staffRoles = (rolesRes.data || [])
          .filter(r => r.isActive !== false)
          .filter(r => !NON_STAFF_SLUGS.has((r.slug || '').toLowerCase()));
        setRbacRoles(staffRoles);
        setRbacPacks(packsRes.data || []);
      } catch (err) {
        console.warn('RBAC fetch failed (fallback to legacy roles):', err);
      } finally {
        if (!cancelled) setRbacLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Liste de rôles formatée pour les Selects
  const dynamicStaffRoles = useMemo(() => {
    if (!rbacRoles || rbacRoles.length === 0) {
      // Fallback legacy
      return [
        { slug: 'operateur', name: 'Opérateur (legacy)', isSystem: true, permissions: [] },
        { slug: 'commercial', name: 'Commercial', isSystem: true, permissions: [] },
        { slug: 'admin', name: 'Administrateur', isSystem: true, permissions: [] },
      ];
    }
    return [...rbacRoles].map(r => {
      // Disambiguation : 'operateur' (legacy) vs 'ops' (canonique RBAC v2)
      const isLegacyOperateur = (r.slug || '').toLowerCase() === 'operateur';
      return isLegacyOperateur ? { ...r, name: `${r.name} (legacy)` } : r;
    }).sort((a, b) => {
      if (a.isSystem !== b.isSystem) return a.isSystem ? -1 : 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [rbacRoles]);

  useEffect(() => {
    // Filter users
    let filtered = users;

    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(u =>
        u.firstName.toLowerCase().includes(query) ||
        u.lastName.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query) ||
        (u.staffNumber && u.staffNumber.toLowerCase().includes(query))
      );
    }

    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to page 1 when filters change
  }, [users, searchQuery, roleFilter]);

  const handleCreateUser = async () => {
    if (!formData.jobTitleId) {
      toast({
        title: "Poste requis",
        description: "Choisissez le poste de la personne dans l'entreprise",
        variant: "destructive"
      });
      return;
    }
    try {
      await axios.post(`${API}/internal-users`, formData);
      
      toast({
        title: "✅ Utilisateur créé",
        description: `${formData.firstName} ${formData.lastName} a été ajouté avec succès`
      });
      
      setShowCreateModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de créer l'utilisateur",
        variant: "destructive"
      });
    }
  };

  const handleDeactivateUser = async (userId, userName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir désactiver ${userName} ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/internal-users/${userId}`);
      
      toast({
        title: "✅ Utilisateur désactivé",
        description: `${userName} a été désactivé avec succès`
      });
      
      fetchUsers();
    } catch (error) {
      console.error('Error deactivating user:', error);
      toast({
        title: "Erreur",
        description: "Impossible de désactiver l'utilisateur",
        variant: "destructive"
      });
    }
  };

  // Bulk selection handlers
  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => {
      const newSelection = prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId];
      
      // Mettre à jour l'état selectAll en fonction de la sélection
      const currentPageUsers = filteredUsers
        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
      
      const allSelected = currentPageUsers.length > 0 && 
        currentPageUsers.every(u => newSelection.includes(u.id));
      setSelectAll(allSelected);
      
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    const currentPageUsers = filteredUsers
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    
    if (selectAll) {
      setSelectedUsers(prev => prev.filter(id => !currentPageUsers.find(u => u.id === id)));
      setSelectAll(false);
    } else {
      const newIds = currentPageUsers.map(u => u.id);
      setSelectedUsers(prev => [...new Set([...prev, ...newIds])]);
      setSelectAll(true);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir désactiver ${selectedUsers.length} utilisateur(s) ?`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/bulk-actions/users/delete`, {
        userIds: selectedUsers
      });
      toast({
        title: "✅ Opération réussie",
        description: response.data.message
      });
      setSelectedUsers([]);
      setSelectAll(false);
      fetchUsers();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'opération bulk",
        variant: "destructive"
      });
    }
  };

  const handleBulkActivate = async () => {
    try {
      const response = await axios.post(`${API}/bulk-actions/users/activate`, {
        userIds: selectedUsers
      });
      toast({
        title: "✅ Utilisateurs activés",
        description: response.data.message
      });
      setSelectedUsers([]);
      setSelectAll(false);
      fetchUsers();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de l'activation",
        variant: "destructive"
      });
    }
  };

  const handleBulkDeactivate = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir désactiver ${selectedUsers.length} utilisateur(s) ?`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/bulk-actions/users/deactivate`, {
        userIds: selectedUsers
      });
      toast({
        title: "✅ Utilisateurs désactivés",
        description: response.data.message
      });
      setSelectedUsers([]);
      setSelectAll(false);
      fetchUsers();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la désactivation",
        variant: "destructive"
      });
    }
  };

  const handleBulkExport = async () => {
    try {
      const response = await axios.post(`${API}/bulk-actions/users/export`, {
        userIds: selectedUsers
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `utilisateurs_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: "✅ Export réussi",
        description: `${selectedUsers.length} utilisateur(s) exporté(s)`
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors de l'export",
        variant: "destructive"
      });
    }
  };

  // Reset selectAll when page changes
  useEffect(() => {
    const currentPageUsers = filteredUsers
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    const allSelected = currentPageUsers.length > 0 && 
      currentPageUsers.every(u => selectedUsers.includes(u.id));
    setSelectAll(allSelected);
  }, [currentPage, filteredUsers, selectedUsers]);

  const handleToggle2FA = async (user) => {
    try {
      const next = !user.twoFactorEnabled;
      await axios.put(`${API}/internal-users/${user.id}`, { twoFactorEnabled: next });
      toast({ title: next ? '2FA activée' : '2FA désactivée', description: `${user.firstName} ${user.lastName}` });
      fetchUsers();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec mise à jour 2FA', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      role: 'operateur',
      jobTitleId: '',
      departement: '',
      manager_id: '',
      team_id: '',
      twoFactorEnabled: false
    });
  };

  // RBAC team-scoping — affectation manager/équipe
  const NONE = '__none__';
  const [teamModalUser, setTeamModalUser] = useState(null);
  const [teamForm, setTeamForm] = useState({ manager_id: NONE, team_id: '' });
  const [teamSaving, setTeamSaving] = useState(false);

  const openTeamModal = (user) => {
    setTeamModalUser(user);
    setTeamForm({ manager_id: user.manager_id || NONE, team_id: user.team_id || '' });
  };

  const handleSaveTeam = async () => {
    if (!teamModalUser) return;
    setTeamSaving(true);
    try {
      await axios.put(`${API}/internal-users/${teamModalUser.id}`, {
        manager_id: teamForm.manager_id === NONE ? '' : teamForm.manager_id,
        team_id: (teamForm.team_id || '').trim(),
      });
      toast({ title: 'Équipe mise à jour', description: `${teamModalUser.firstName} ${teamModalUser.lastName}` });
      setTeamModalUser(null);
      fetchUsers();
    } catch (error) {
      toast({ title: 'Erreur', description: error?.response?.data?.detail || "Échec de l'affectation.", variant: 'destructive' });
    } finally {
      setTeamSaving(false);
    }
  };

  const getRoleBadge = (role) => {
    const roleObj = dynamicStaffRoles.find(r => r.slug === role);
    const Icon = ROLE_ICON_BY_SLUG[role] || Shield;
    const label = roleObj?.name || role || '—';
    const isCustom = roleObj && roleObj.isSystem === false;

    return (
      <Badge
        variant="outline"
        className={`border-slate-300 text-slate-700 bg-white inline-flex items-center gap-1.5 ${isCustom ? 'ring-1 ring-violet-200' : ''}`}
        title={roleObj?.description || label}
      >
        <Icon className="w-3 h-3" />
        {label}
        {isCustom && (
          <span className="ml-0.5 px-1 py-0 text-[9px] uppercase rounded bg-violet-100 text-violet-700 border border-violet-200">
            Custom
          </span>
        )}
      </Badge>
    );
  };

  const stats = {
    total: users.length,
    operators: users.filter(u => ['operateur', 'ops', 'lead_ops'].includes(u.role)).length,
    commercials: users.filter(u => ['commercial', 'lead_commercial'].includes(u.role)).length,
    admins: users.filter(u => u.role === 'admin').length,
    active: users.filter(u => u.isActive).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1db584]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-3xl font-bold text-gray-900">Gestion des Utilisateurs Internes</h1>
          <p className="text-gray-500 mt-1">Gérer les administrateurs, commerciaux et opérateurs</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg border bg-white p-0.5">
            <button
              type="button"
              onClick={() => setView('list')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${view === 'list' ? 'bg-[#1db584] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              data-testid="view-list-btn"
            >
              Liste
            </button>
            <button
              type="button"
              onClick={() => setView('org')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${view === 'org' ? 'bg-[#1db584] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              data-testid="view-org-btn"
            >
              Organigramme
            </button>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#1db584] hover:bg-[#16a075]"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Nouvel Utilisateur
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Users className="w-8 h-8 mx-auto text-gray-500 mb-2" />
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <User className="w-8 h-8 mx-auto text-green-500 mb-2" />
              <p className="text-sm text-gray-600">Opérateurs</p>
              <p className="text-2xl font-bold text-green-600">{stats.operators}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Briefcase className="w-8 h-8 mx-auto text-blue-500 mb-2" />
              <p className="text-sm text-gray-600">Commerciaux</p>
              <p className="text-2xl font-bold text-blue-600">{stats.commercials}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Shield className="w-8 h-8 mx-auto text-purple-500 mb-2" />
              <p className="text-sm text-gray-600">Admins</p>
              <p className="text-2xl font-bold text-purple-600">{stats.admins}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="w-8 h-8 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
              <p className="text-sm text-gray-600">Actifs</p>
              <p className="text-2xl font-bold">{stats.active}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {view === 'org' && <OrgChart users={users} />}

      {view === 'list' && (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Liste des Utilisateurs ({filteredUsers.length})</CardTitle>
            <div className="flex gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-[250px]"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filtrer par rôle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les rôles</SelectItem>
                  {dynamicStaffRoles.map(r => {
                    const Ic = ROLE_ICON_BY_SLUG[r.slug] || Shield;
                    return (
                      <SelectItem key={r.slug} value={r.slug}>
                        <span className="inline-flex items-center gap-2">
                          <Ic className="w-3.5 h-3.5 text-slate-500" />
                          {r.name}
                          {!r.isSystem && (
                            <span className="ml-1 px-1 text-[9px] uppercase rounded bg-violet-100 text-violet-700">Custom</span>
                          )}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox 
                    checked={selectAll}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead>Numéro Staff</TableHead>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Téléphone</TableHead>
                <TableHead>Poste</TableHead>
                <TableHead>Rôle</TableHead>
                <TableHead>Département</TableHead>
                <TableHead data-testid="users-manager-col">Manager / Équipe</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-gray-500">
                    Aucun utilisateur trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((user) => (
                  <TableRow key={user.id} className={selectedUsers.includes(user.id) ? 'bg-blue-50' : ''}>
                    <TableCell>
                      <Checkbox 
                        checked={selectedUsers.includes(user.id)}
                        onCheckedChange={() => handleSelectUser(user.id)}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm">{user.staffNumber}</TableCell>
                    <TableCell className="font-medium">
                      {user.firstName} {user.lastName}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell data-testid={`user-jobtitle-cell-${user.id}`}>
                      {user.jobTitle || <span className="text-gray-400 italic text-xs">À renseigner</span>}
                    </TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell>{user.departement || '-'}</TableCell>
                    <TableCell data-testid={`user-manager-cell-${user.id}`}>
                      {(() => {
                        const m = users.find((u) => u.id === user.manager_id);
                        return m
                          ? <span className="text-sm text-gray-700">{m.firstName} {m.lastName}</span>
                          : <span className="text-gray-400">-</span>;
                      })()}
                      {user.team_id ? (
                        <Badge variant="outline" className="ml-2 text-[10px] align-middle">{user.team_id}</Badge>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {user.isActive ? (
                        <Badge className="bg-green-100 text-green-800 border-0">Actif</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-800 border-0">Inactif</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Affecter manager / équipe"
                          data-testid={`assign-team-btn-${user.id}`}
                          onClick={() => openTeamModal(user)}
                        >
                          <Users className="w-4 h-4 text-[#1db584]" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title={user.twoFactorEnabled ? 'Désactiver la 2FA' : 'Activer la 2FA'}
                          data-testid={`toggle-2fa-${user.id}`}
                          onClick={() => handleToggle2FA(user)}
                        >
                          <Shield className={`w-4 h-4 ${user.twoFactorEnabled ? 'text-emerald-600' : 'text-gray-300'}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Modifier le poste"
                          data-testid={`edit-jobtitle-${user.id}`}
                          onClick={() => {
                            setJobTitleModalUser(user);
                            setJobTitleModalValue(user.jobTitleId || '');
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {user.isActive && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeactivateUser(user.id, `${user.firstName} ${user.lastName}`)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          <TablePagination
            currentPage={currentPage}
            totalItems={filteredUsers.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            itemLabel="utilisateurs"
          />
        </CardContent>
      </Card>
      )}

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouvel Utilisateur Interne</DialogTitle>
            <DialogDescription>
              Créer un nouveau compte pour un administrateur, commercial ou opérateur
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Prénom *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  placeholder="Prénom"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Nom *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  placeholder="Nom"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="email@baobabco.net"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Téléphone *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+225 XX XX XX XX XX"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="Minimum 6 caractères"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role">Rôle *</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {dynamicStaffRoles.map(r => {
                    const Ic = ROLE_ICON_BY_SLUG[r.slug] || Shield;
                    return (
                      <SelectItem key={r.slug} value={r.slug}>
                        <span className="inline-flex items-center gap-2">
                          <Ic className="w-3.5 h-3.5 text-slate-500" />
                          {r.name}
                          {!r.isSystem && (
                            <span className="ml-1 px-1 text-[9px] uppercase rounded bg-violet-100 text-violet-700">Custom</span>
                          )}
                          <span className="text-[10px] text-slate-400">· {(r.permissions || []).length} perms</span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500">
                {rbacLoading ? 'Chargement des rôles…' : `${dynamicStaffRoles.length} rôles staff disponibles (système + custom)`}
              </p>
            </div>

            {/* Templates de rôles par population — création rapide depuis un pack prédéfini */}
            {rbacPacks.length > 0 && (
              <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <Label className="text-sm font-medium text-slate-700 m-0">
                    Templates de rôles par population
                  </Label>
                </div>
                <p className="text-[11px] text-slate-500 -mt-1">
                  Aucun rôle ne correspond ? Créez-en un depuis un template prédéfini.
                </p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {rbacPacks.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => navigate(`/admin/roles?fromPack=${encodeURIComponent(p.slug)}`)}
                      className="flex items-start gap-2 px-2.5 py-2 rounded-md border border-slate-200 bg-white hover:border-emerald-400 hover:bg-emerald-50/40 transition text-left"
                      data-testid={`tpl-pack-${p.slug}`}
                    >
                      <Layers className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-medium text-slate-800 truncate flex items-center gap-1">
                          {p.name}
                          <ExternalLink className="w-3 h-3 text-slate-400" />
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">{(p.permissions || []).length} permissions</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="jobTitle">Poste dans l&apos;entreprise *</Label>
              <Select
                value={formData.jobTitleId}
                onValueChange={(v) => setFormData({ ...formData, jobTitleId: v })}
              >
                <SelectTrigger id="jobTitle" data-testid="create-jobtitle-select">
                  <SelectValue placeholder="Choisir un poste…" />
                </SelectTrigger>
                <SelectContent>
                  {jobTitles.length === 0 ? (
                    <SelectItem value="__none__" disabled>
                      Aucun poste défini — Paramètres → Postes
                    </SelectItem>
                  ) : jobTitles.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`create-jobtitle-option-${t.id}`}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                La fonction réelle de la personne. Le rôle ci-dessus ne gère que les droits d&apos;accès.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="departement">Département (optionnel)</Label>
              <Input
                id="departement"
                list="departement-suggestions"
                value={formData.departement}
                onChange={(e) => setFormData({...formData, departement: e.target.value})}
                placeholder="Ex: Opérations"
                data-testid="create-departement-input"
              />
              <datalist id="departement-suggestions">
                {existingDepartments.map((d) => (
                  <option key={d} value={d} />
                ))}
              </datalist>
              <p className="text-xs text-gray-500">
                Choisissez un département existant pour éviter les doublons d&apos;orthographe.
              </p>
            </div>

            <div className="flex items-start gap-3 p-3 border rounded-lg bg-gray-50">
              <Checkbox
                id="twofa"
                checked={formData.twoFactorEnabled}
                onCheckedChange={(v) => setFormData({...formData, twoFactorEnabled: !!v})}
                data-testid="create-2fa-checkbox"
                className="mt-0.5"
              />
              <Label htmlFor="twofa" className="cursor-pointer">
                <span className="flex items-center gap-1.5 font-medium"><Shield className="w-4 h-4 text-emerald-600" />Authentification à deux facteurs (2FA)</span>
                <span className="block text-xs text-gray-500 mt-0.5">Exige un code OTP (WhatsApp/Email) à la connexion. Recommandé pour les comptes sensibles. (Comptes locaux uniquement)</span>
              </Label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="create-manager">Manager (optionnel)</Label>
                <Select
                  value={formData.manager_id || '__none__'}
                  onValueChange={(v) => setFormData({...formData, manager_id: v === '__none__' ? '' : v})}
                >
                  <SelectTrigger id="create-manager" data-testid="create-manager-select">
                    <SelectValue placeholder="Aucun" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Aucun</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.firstName} {u.lastName} · {u.role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-team">Équipe (optionnel)</Label>
                <Input
                  id="create-team"
                  data-testid="create-team-input"
                  value={formData.team_id}
                  onChange={(e) => setFormData({...formData, team_id: e.target.value})}
                  placeholder="Ex: equipe-abidjan-1"
                />
              </div>
            </div>
            <p className="text-xs text-gray-400">
              Manager / Équipe servent au <strong>data-scoping « équipe »</strong> : un responsable en scope « Équipe » voit les opérations de ses subordonnés.
            </p>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={handleCreateUser}
                className="bg-[#1db584] hover:bg-[#16a075]"
                disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.password}
              >
                Créer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Actions Bar */}
      <GenericBulkActionsBar
        entityType="users"
        selectedCount={selectedUsers.length}
        onClearSelection={() => { setSelectedUsers([]); setSelectAll(false); }}
        onDelete={handleBulkDelete}
        onActivate={handleBulkActivate}
        onDeactivate={handleBulkDeactivate}
        onExport={handleBulkExport}
      />

      {/* Team assignment modal (RBAC team-scoping) */}
      <Dialog open={!!teamModalUser} onOpenChange={(o) => !o && setTeamModalUser(null)}>
        <DialogContent className="max-w-md" data-testid="assign-team-modal">
          <DialogHeader>
            <DialogTitle>Affecter manager / équipe</DialogTitle>
            <DialogDescription>
              {teamModalUser ? `${teamModalUser.firstName} ${teamModalUser.lastName} · ${teamModalUser.role}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="assign-manager">Manager (responsable hiérarchique)</Label>
              <Select
                value={teamForm.manager_id}
                onValueChange={(v) => setTeamForm({ ...teamForm, manager_id: v })}
              >
                <SelectTrigger id="assign-manager" data-testid="assign-manager-select">
                  <SelectValue placeholder="Aucun" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Aucun</SelectItem>
                  {users.filter((u) => !teamModalUser || u.id !== teamModalUser.id).map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.firstName} {u.lastName} · {u.jobTitle || u.departement || 'Poste à renseigner'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="assign-team">Équipe (identifiant)</Label>
              <Input
                id="assign-team"
                data-testid="assign-team-input"
                value={teamForm.team_id}
                onChange={(e) => setTeamForm({ ...teamForm, team_id: e.target.value })}
                placeholder="Ex: equipe-abidjan-1"
              />
            </div>
            <p className="text-xs text-gray-400">
              Utilisé par le <strong>data-scoping « Équipe »</strong> (Admin &gt; Rôles &gt; Data-scoping). Un manager en scope « Équipe » voit les enregistrements assignés à lui-même + ses subordonnés.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setTeamModalUser(null)}>Annuler</Button>
              <Button
                onClick={handleSaveTeam}
                disabled={teamSaving}
                className="bg-[#1db584] hover:bg-[#16a075]"
                data-testid="assign-team-save-btn"
              >
                {teamSaving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modifier le poste d'une personne existante */}
      <Dialog open={!!jobTitleModalUser} onOpenChange={(o) => !o && setJobTitleModalUser(null)}>
        <DialogContent data-testid="edit-jobtitle-dialog">
          <DialogHeader>
            <DialogTitle>Poste dans l&apos;entreprise</DialogTitle>
            <DialogDescription>
              {jobTitleModalUser ? `${jobTitleModalUser.firstName} ${jobTitleModalUser.lastName}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={jobTitleModalValue} onValueChange={setJobTitleModalValue}>
              <SelectTrigger data-testid="edit-jobtitle-select">
                <SelectValue placeholder="Choisir un poste…" />
              </SelectTrigger>
              <SelectContent>
                {jobTitles.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`edit-jobtitle-option-${t.id}`}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Le poste n&apos;affecte aucun droit d&apos;accès. Les postes se gèrent dans
              Paramètres → Postes.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setJobTitleModalUser(null)}>Annuler</Button>
              <Button
                onClick={saveJobTitle}
                disabled={savingJobTitle}
                className="bg-[#1db584] hover:bg-[#16a075]"
                data-testid="edit-jobtitle-save-btn"
              >
                {savingJobTitle ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InternalUsersManagement;
